/**
 * alignedBlocks.ts — PLAT-10264 (T5, epic PLAT-10209)
 *
 * Pure block-construction for the Translation Review workspace's Segment and
 * Fragment view modes. Both modes render the original and translated columns
 * as aligned rows; this module turns the store's segment/fragment data into the
 * row model (`AlignedBlock[]`) the `AlignedBlocksView` lays out.
 *
 * Scope note (PLAT-10264 — the Option-A reversal): this module produces only the
 * row model. The view's prose-parity affordances on top of these rows — per-
 * block speaker headers + timestamps and the source-side redline — are derived
 * in `AlignedBlocksView` from each block's `lead` element (`speaker_label` /
 * `ts`) and the elements' `state`, NOT here. The block model is unchanged by
 * that reversal; a block stays `{id, originalIndices, translatedValue, lead}`.
 *
 * No React, no MobX, no store imports — a deterministic projection over plain
 * data so it can be unit-tested in isolation (per the epic's "Segment view is a
 * projection, never a separate store of truth" invariant).
 *
 * Terminology (epic doc): a paragraph ⊃ a segment ⊃ a fragment.
 *   - Segment mode → one block per `TranslationSegment` (active fragments
 *     grouped by `parentId`; `getSegments` already did the grouping + ordering
 *     and the active-set filter).
 *   - Fragment mode → one block per active `TranslationPhrase` (the fine,
 *     word-level tiling persisted after a split).
 *
 * Pre-split equivalence: when nothing has been split, `getSegments` returns one
 * single-fragment segment per phrase, so Segment and Fragment produce identical
 * blocks — Fragment mode is selectable but matches Segment until a split runs.
 */

import { joinPhraseValues } from './utils';

import type {
  GroupWithSpeakerInfo,
  TranslatedSortedPhrase,
} from './hooks/useTranslatedRender';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type {
  TranslationPhrase,
  TranslationSegment,
} from '@src/types.ts/translation';

/** The three segmentation granularities offered by the view-mode toggle. */
export type TranslationViewMode = 'paragraph' | 'segment' | 'fragment';

/**
 * Validate a raw view-mode URL value (else undefined). The value rides in each
 * surface's review param — `?translationReview=` / `?clipTranslationReview=`.
 */
export const parseTranslationViewMode = (
  value: string | null,
): TranslationViewMode | undefined =>
  value === 'paragraph' || value === 'segment' || value === 'fragment'
    ? value
    : undefined;

/**
 * One aligned row. The same block id/originalIndices drive BOTH columns: the
 * original column renders the source elements at `originalIndices`; the
 * translated column renders `translatedValue`. `lead` (the smallest source
 * index) is both the row's sort key and the value the translated word atom
 * stamps on `data-initial-index` — so it also matches `useTranslatedRender`'s
 * `currentLead` for the active-row highlight.
 */
export interface AlignedBlock {
  /** Stable React key + lockstep-highlight identity. */
  id: string;
  /** Source `initial_index` values this block covers, ascending, de-duped. */
  originalIndices: number[];
  /**
   * PLAT-10611 — the stable element UUIDs this block covers (union of its
   * phrase(s)' `originalElementIds`, content-only). Parallel membership key to
   * `originalIndices`: `blockInScope` intersects a stable-id clip scope by these
   * FIRST, falling back to the integer indices. Absent on legacy blobs whose
   * phrases carry no ids.
   */
  originalElementIds?: string[];
  /**
   * PLAT-10266 (2026-07-28) — the block's OWN min..max ARRAY POSITION span, set for
   * PARAGRAPH blocks only.
   *
   * PLAT-11717 — WHICH ARRAY: positions into the `sourceElements` array the
   * original column RENDERS, never into `useTranslatedRender`'s `filteredElements`
   * (which is what the groups' own `elements[].index` are — see that hook's
   * contract). The two diverge by every dropped whitespace / cut / removed
   * element, so `buildParagraphBlocks` translates the group positions before
   * emitting them here. Emitting the untranslated ones put each paragraph's span
   * roughly half an array too early: a row's own replaced word fell past its `to`
   * and vanished, while the NEXT row's `from` let its leading walk reach back over
   * the boundary and render that same word a second time (the `toro.com/intelli360`
   * corruption of PLAT-11682).
   *
   * Why: `renderOriginalWords` anchors its window on the covered identities that
   * still resolve, and a replaced word is spliced in with `initial_index:-1` and no
   * `source_uuid`, so it anchors nothing. Paragraph blocks additionally carry NO
   * `originalElementIds`, so there is no vanished key to detect either — a word
   * replaced at a paragraph's LEADING edge simply fell outside the window and
   * disappeared from the review's original column (Denys 2026-07-28). This span is
   * the authority that lets the window reach its own inserted tokens while stopping
   * dead at the paragraph boundary, so a neighbouring paragraph's inserts are never
   * absorbed (which would render the same words in two rows).
   */
  arraySpan?: { from: number; to: number };
  /** The block's translated text (segment parent value, or joined fragments). */
  translatedValue: string;
  /**
   * Per-phrase translated units in lead order, populated for ALL view modes
   * (Paragraph aggregates a group's phrases; Segment its fragments; Fragment a
   * single phrase). The translated column renders each as its own click-to-seek
   * target (seeking to its `lead`) AND its own right-click "Edit translation"
   * target — the popover edits the carried `phrase` by its `originalIndices`,
   * matching the inline prose view (PLAT-10272 edit-parity in the workspace).
   * `value` is the phrase's `translatedValue` (coalesced to '') and is kept for
   * the spacing + active-phrase-highlight reads. Empty/whitespace phrases are
   * NOT dropped — parity with the inline view, which renders them with their
   * `data-initial-index` anchors.
   */
  translatedPhrases?: {
    lead: number;
    value: string;
    phrase: TranslationPhrase;
  }[];
  /** Smallest `originalIndices` value — sort key + `data-initial-index` anchor. */
  lead: number;
}

/**
 * Clip-scope descriptor. Mirrors `useTranslatedRender`'s `TranslatedInScope`:
 * the active source-index `Set` plus an OPTIONAL inclusive `[minIdx, maxIdx]`
 * lead range. A block is in scope when its indices intersect `activeIndices`
 * (the same intersection the prose `inScopePhrases` uses); when the range is
 * present a block is also kept if its `lead` sits inside it. The aligned view's
 * clip-scoped mode passes a bare `ReadonlySet<number>` (intersection only — its
 * clip-local `activeIndices` already covers in-clip cut/removed, so no range is
 * needed and a global range would leak across montage cut-gaps, PLAT-10209
 * H-A). `buildAlignedBlocks` accepts either form.
 */
export interface AlignedBlocksScope {
  activeIndices: ReadonlySet<number>;
  /**
   * PLAT-10611 — the stable element UUIDs in scope (parallel to `activeIndices`).
   * When present, `blockInScope` intersects a block's `originalElementIds`
   * against it FIRST — drift-correct where a clip's `initial_index`es have
   * shifted from story space — then falls back to the integer intersection.
   */
  activeUuids?: ReadonlySet<string>;
  minIdx?: number;
  maxIdx?: number;
}

/**
 * Normalize the scope argument: a bare index `Set` becomes a descriptor with no
 * range (intersection-only), a descriptor passes through. `undefined` →
 * `undefined` (no scoping: keep every block).
 */
function normalizeScope(
  scope: ReadonlySet<number> | AlignedBlocksScope | undefined,
): AlignedBlocksScope | undefined {
  if (scope === undefined) return undefined;
  if (scope instanceof Set) {
    return { activeIndices: scope };
  }
  return scope as AlignedBlocksScope;
}

/** Smallest index in a list, or +Infinity for an empty list (sorts last). */
function minIndex(indices: readonly number[]): number {
  let min = Infinity;
  for (const i of indices) if (i < min) min = i;
  return min;
}

/**
 * Sorted (ascending) union of every fragment's `originalIndices` in a segment.
 * `getSegments` already filters to active fragments and orders them, but a
 * fragment's own `originalIndices` are not guaranteed contiguous/sorted across
 * the segment, so we union + sort defensively.
 */
function unionFragmentIndices(
  fragments: readonly TranslationPhrase[],
): number[] {
  const set = new Set<number>();
  for (const f of fragments) {
    for (const idx of f.originalIndices) set.add(idx);
  }
  return [...set].sort((a, b) => a - b);
}

/**
 * PLAT-10611 — union of every fragment's `originalElementIds` (content-only
 * stable UUIDs) in a segment. `undefined` when no fragment carries ids (legacy
 * blob) so a block on old data stays free of an empty id array.
 */
function unionFragmentElementIds(
  fragments: readonly TranslationPhrase[],
): string[] | undefined {
  const set = new Set<string>();
  for (const f of fragments) {
    if (f.originalElementIds)
      for (const id of f.originalElementIds) set.add(id);
  }
  return set.size > 0 ? [...set] : undefined;
}

/** A fragment's own indices, ascending + de-duped. */
function fragmentIndices(phrase: TranslationPhrase): number[] {
  return [...new Set(phrase.originalIndices)].sort((a, b) => a - b);
}

/**
 * Project fragments into a block's per-phrase translated units (lead-sorted;
 * orphan fragments with no source index dropped so they never stamp an Infinity
 * `data-initial-index`). Carries the full `TranslationPhrase` so the translated
 * column can right-click-edit each unit (PLAT-10272 workspace edit-parity).
 */
function fragmentsToTranslatedPhrases(
  fragments: readonly TranslationPhrase[],
): { lead: number; value: string; phrase: TranslationPhrase }[] {
  return fragments
    .map(f => ({
      lead: minIndex(fragmentIndices(f)),
      value: f.translatedValue ?? '',
      phrase: f,
    }))
    .filter(p => Number.isFinite(p.lead))
    .sort((a, b) => a.lead - b.lead);
}

/**
 * Segment block translated text. Prefer the segment record's pre-fragmenting
 * `translatedValue` (the coarse unit the LLM/SRT produced); fall back to
 * joining the active fragments' values in source order when there is no parent
 * (unsplit `solo:` segments, where the single fragment IS the segment).
 */
function segmentTranslatedValue(segment: TranslationSegment): string {
  const parentValue = segment.parent?.translatedValue;
  if (typeof parentValue === 'string' && parentValue.length > 0) {
    return parentValue;
  }
  return joinPhraseValues(
    segment.fragments
      .map(f => f.translatedValue)
      .filter((v): v is string => typeof v === 'string' && v.length > 0),
  );
}

/** Does any of `indices` appear in `inScope`? (Empty `indices` → false.) */
function intersectsScope(
  indices: readonly number[],
  inScope: ReadonlySet<number>,
): boolean {
  for (const idx of indices) if (inScope.has(idx)) return true;
  return false;
}

/** Does any of `ids` appear in `inScope`? (Empty `ids` → false.) */
function intersectsUuidScope(
  ids: readonly string[],
  inScope: ReadonlySet<string>,
): boolean {
  for (const id of ids) if (inScope.has(id)) return true;
  return false;
}

/**
 * The clip-scope membership test (PLAT-10209 H-A). A block is in scope when any
 * of its source indices is in the active set — a pure intersection (the same
 * test the prose `inScopePhrases` uses). The optional `[minIdx, maxIdx]` lead
 * range is applied ONLY when present; a bare-`Set` scope omits it, giving
 * intersection-only. (The clip-scoped aligned view passes a bare Set: its
 * clip-local `activeIndices` already carries the in-clip cut/removed words, and
 * a global range would leak across montage cut-gaps.) See
 * {@link buildAlignedBlocks}.
 */
function blockInScope(block: AlignedBlock, scope: AlignedBlocksScope): boolean {
  // PLAT-10611 — stable-id intersection FIRST (drift-correct): a block is in
  // scope when one of its element UUIDs is in the active id set. Only applied
  // when both sides carry ids; otherwise (legacy blobs / bare-`Set` scope) the
  // integer intersection below is authoritative — so a block is never dropped by
  // an id miss (the integer/range fallback still runs).
  if (
    scope.activeUuids &&
    block.originalElementIds &&
    intersectsUuidScope(block.originalElementIds, scope.activeUuids)
  ) {
    return true;
  }
  if (intersectsScope(block.originalIndices, scope.activeIndices)) return true;
  if (scope.minIdx === undefined || scope.maxIdx === undefined) return false;
  return block.lead >= scope.minIdx && block.lead <= scope.maxIdx;
}

/**
 * Build the aligned-row model for a view mode.
 *
 * - `'segment'` → one block per segment; `originalIndices` = sorted union of
 *   the segment's fragments' indices; `translatedValue` = parent value or
 *   joined fragment values; `id` = the segment id.
 * - `'fragment'` → one block per active phrase; `originalIndices` = the
 *   phrase's own indices; `id` = `frag:<lead>` (lead is unique per phrase).
 * - `'paragraph'` → `[]`. Paragraph mode renders the existing prose panes, not
 *   aligned blocks, so it has no block model here.
 *
 * Blocks with no source indices (`lead === Infinity`) are dropped — they have
 * no row to align to. Output is sorted ascending by `lead`.
 *
 * Clip-scoping (PLAT-10209 H-A): `scope`, when provided, describes the source
 * content that is actually in the clip. The translation store is NOT
 * clip-proxied, so `segments` / `activePhrases` describe the WHOLE story while
 * only the clip's content is on screen — without scoping, whole-story
 * translation (including PRE-CLIP rows) renders. A block is kept when its
 * `originalIndices` intersect `scope.activeIndices` — the same intersection the
 * prose `inScopePhrases` applies (see {@link blockInScope}). The clip-scoped
 * aligned view passes a bare `ReadonlySet<number>`: its clip-local active set
 * already covers in-clip cut/removed words, so a struck in-clip block
 * intersects it directly — no range needed, and a global range would leak
 * across montage cut-gaps. The optional `[minIdx, maxIdx]` range is applied
 * only when a full descriptor is passed. Omitting `scope` keeps every block.
 */
export function buildAlignedBlocks(
  viewMode: TranslationViewMode,
  segments: readonly TranslationSegment[],
  activePhrases: readonly TranslationPhrase[],
  scope?: ReadonlySet<number> | AlignedBlocksScope,
): AlignedBlock[] {
  let blocks: AlignedBlock[];

  if (viewMode === 'segment') {
    blocks = segments.map(segment => {
      const originalIndices = unionFragmentIndices(segment.fragments);
      return {
        id: segment.id,
        originalIndices,
        originalElementIds: unionFragmentElementIds(segment.fragments),
        translatedValue: segmentTranslatedValue(segment),
        // A segment's per-phrase units ARE its fragments — so the translated
        // column renders + edits + seeks per fragment, identical to the inline
        // prose view (which has no segment grouping; it renders the fragment
        // stream). This also makes Segment-view highlight/seek per-fragment.
        translatedPhrases: fragmentsToTranslatedPhrases(segment.fragments),
        lead: minIndex(originalIndices),
      };
    });
  } else if (viewMode === 'fragment') {
    blocks = activePhrases.map(phrase => {
      const originalIndices = fragmentIndices(phrase);
      const lead = minIndex(originalIndices);
      return {
        id: `frag:${lead}`,
        originalIndices,
        originalElementIds:
          phrase.originalElementIds && phrase.originalElementIds.length > 0
            ? [...phrase.originalElementIds]
            : undefined,
        translatedValue: phrase.translatedValue ?? '',
        // A fragment block is a single phrase; carry it so the translated Word
        // is its own right-click "Edit translation" target (edit-parity).
        translatedPhrases: [
          { lead, value: phrase.translatedValue ?? '', phrase },
        ],
        lead,
      };
    });
  } else {
    // Paragraph mode uses the existing prose renderers, not aligned blocks.
    return [];
  }

  const normalizedScope = normalizeScope(scope);
  return blocks
    .filter(b => Number.isFinite(b.lead))
    .filter(
      b => normalizedScope === undefined || blockInScope(b, normalizedScope),
    )
    .sort((a, b) => a.lead - b.lead);
}

/**
 * PLAT-11717 — element -> its position in the array the original column renders.
 *
 * The bridge between the two coordinate systems a paragraph block sits between:
 * the groups' `elements[].index` are positions into `useTranslatedRender`'s
 * `filteredElements`, while `arraySpan`'s only consumer (`renderOriginalWords`)
 * reads positions into `sourceElements`. Identity is the exact conversion —
 * `filteredElements` is a `.filter()` of `sourceElements`, so a group holds the
 * very same element objects the rendered array does.
 *
 * First occurrence wins, matching the view's own `posByInitialIndex` /
 * `posBySourceUuid` memos in spirit; the rendered array never repeats an element.
 */
function sourcePositionsOf(
  sourceElements: readonly TranscriptElement[] | undefined,
): Map<TranscriptElement, number> | undefined {
  if (!sourceElements) return undefined;
  const map = new Map<TranscriptElement, number>();
  sourceElements.forEach((element, pos) => {
    if (!map.has(element)) map.set(element, pos);
  });
  return map;
}

/** A paragraph group's own source positions, ascending, de-duped, negatives dropped. */
function groupSourceIndices(group: GroupWithSpeakerInfo): number[] {
  const indexSet = new Set<number>();
  for (const { element } of group.elements) {
    if (element.initial_index < 0) continue;
    indexSet.add(element.initial_index);
  }
  return [...indexSet].sort((a, b) => a - b);
}

/**
 * PLAT-10945 — which paragraph groups were ABSORBED by a correction that spans
 * a paragraph boundary, and which group's row their words belong on.
 *
 * A span correction covering two source paragraphs is ONE phrase, and
 * `useTranslatedRender` assigns it to the first group whose elements resolve to
 * it. The other group is then handed no translated phrase at all, and
 * {@link buildParagraphBlocks} used to emit its row with the original-language
 * words present and the translated side EMPTY — which reads to the person on
 * that screen as their translation having been deleted.
 *
 * The row cannot simply be dropped: its original-language words are real content
 * and both panes are drawn from this one block list, so dropping the block would
 * remove those words from the screen. Instead the absorbed group's source
 * positions are folded onto the row that carries the correction covering them,
 * so one row shows both paragraphs' words beside the one correction. This is the
 * same fold {@link buildRunGroupedBlocks} already applies to a run of moved words
 * with no fragment of their own (step 3 there), at paragraph granularity.
 *
 * Resolution is UUID-first (`source_uuid ?? id` against each phrase's
 * `originalElementIds`) with the integer `initial_index` as the per-element
 * fallback — the same drift-correct precedence the rest of PLAT-10611 uses.
 *
 * Three deliberate refusals, each of which keeps today's row instead:
 *  - **No covering phrase anywhere.** A genuinely untranslated paragraph has
 *    nothing to fold onto, and its empty translated side is honest.
 *  - **A different speaker.** Cards 1 and 2 of PLAT-10945 make a cross-speaker
 *    correction impossible to author, so this can only arrive in pre-existing
 *    machine data; folding it would put one person's words under another's name
 *    and drop that speaker's header row.
 *  - **Not contiguous.** The absorbed groups plus their target must form an
 *    unbroken run of group positions. `renderOriginalWords` draws a block from
 *    the source ARRAY SLICE spanning its positions, so folding across a group
 *    that keeps its own translation would repeat that group's words on two rows
 *    (the duplicated-original defect PLAT-10611 fixed for segments). A
 *    correction is authored from one contiguous on-screen selection, so a real
 *    correction's groups are contiguous in this array in BOTH hosts — story
 *    order for Content Studio, playback order for the clip-scoped view.
 */
function resolveAbsorbedParagraphs(
  groups: readonly GroupWithSpeakerInfo[],
  phrasesByGroup: readonly TranslatedSortedPhrase[][],
): {
  absorbed: ReadonlySet<number>;
  extraIndicesByTarget: ReadonlyMap<number, number[]>;
  // PLAT-10266: the absorbed GROUP ordinals per target. `extraIndicesByTarget`
  // carries only source indices, which a replaced word does not have — the
  // caller needs the groups themselves to fold their ARRAY span too.
  membersByTarget: ReadonlyMap<number, number[]>;
} {
  const empty = {
    absorbed: new Set<number>(),
    extraIndicesByTarget: new Map<number, number[]>(),
    membersByTarget: new Map<number, number[]>(),
  };
  const candidates: number[] = [];
  groups.forEach((_, i) => {
    if ((phrasesByGroup[i] ?? []).length === 0) candidates.push(i);
  });
  if (candidates.length === 0) return empty;

  // Every covered element key of every DISTRIBUTED phrase -> the group whose row
  // renders that phrase. A candidate group holds no phrase, so it can never be
  // its own target.
  const groupByUuid = new Map<string, number>();
  const groupByIndex = new Map<number, number>();
  groups.forEach((_, i) => {
    for (const { phrase } of phrasesByGroup[i] ?? []) {
      if (phrase.originalElementIds) {
        for (const id of phrase.originalElementIds) {
          if (!groupByUuid.has(id)) groupByUuid.set(id, i);
        }
      }
      for (const idx of phrase.originalIndices) {
        if (!groupByIndex.has(idx)) groupByIndex.set(idx, i);
      }
    }
  });

  // Proposed folds, before the contiguity check.
  const targetByAbsorbed = new Map<number, number>();
  for (const i of candidates) {
    const targets = new Set<number>();
    for (const { element } of groups[i].elements) {
      const uuid = element.source_uuid ?? element.id;
      const byUuid = uuid !== undefined ? groupByUuid.get(uuid) : undefined;
      const target =
        byUuid ??
        (element.initial_index >= 0
          ? groupByIndex.get(element.initial_index)
          : undefined);
      if (target !== undefined) targets.add(target);
    }
    // One unambiguous target only. Two would mean two separate corrections each
    // reaching into this paragraph, where there is no single row its words
    // belong on — keep the row rather than guess.
    if (targets.size !== 1) continue;
    const [target] = targets;
    const absorbedSpeaker = groups[i].speakerLabel;
    const targetSpeaker = groups[target].speakerLabel;
    // Compare only labels that are SET: punctuation-only groups carry none, and
    // comparing an unset label would refuse a legitimate fold.
    if (absorbedSpeaker && targetSpeaker && absorbedSpeaker !== targetSpeaker) {
      continue;
    }
    if (groups[i].elements.length === 0) continue;
    targetByAbsorbed.set(i, target);
  }
  if (targetByAbsorbed.size === 0) return empty;

  // Contiguity: keep a target's folds only when that target and everything
  // folded onto it occupy an unbroken run of group positions.
  const byTarget = new Map<number, number[]>();
  for (const [absorbedIdx, target] of targetByAbsorbed) {
    const list = byTarget.get(target);
    if (list) list.push(absorbedIdx);
    else byTarget.set(target, [absorbedIdx]);
  }
  const absorbed = new Set<number>();
  const extraIndicesByTarget = new Map<number, number[]>();
  const membersByTarget = new Map<number, number[]>();
  for (const [target, members] of byTarget) {
    const run = [target, ...members].sort((a, b) => a - b);
    if (run[run.length - 1] - run[0] !== run.length - 1) continue;
    const extra: number[] = [];
    for (const m of members) {
      absorbed.add(m);
      extra.push(...groupSourceIndices(groups[m]));
    }
    extraIndicesByTarget.set(target, extra);
    membersByTarget.set(target, members);
  }
  return { absorbed, extraIndicesByTarget, membersByTarget };
}

/**
 * Build the aligned-row model for **Paragraph** view — PLAT-10288 (a
 * refinement of T5). One block per paragraph group, so each paragraph becomes a
 * single aligned row across both panes (the taller language sets the row
 * height). This is the SAME aligned-row mechanism Segment/Fragment use, applied
 * at paragraph granularity (epic decision: Option A — bare word-rows, no
 * speaker headers / prose flow).
 *
 * Inputs come straight from `useTranslatedRender`, index-parallel:
 *   - `groups[i]` — the paragraph/speaker group with its source elements.
 *   - `phrasesByGroup[i]` — that group's translated phrases, ALREADY
 *     clip-scope-filtered and distributed by `useTranslatedRender` (lead order).
 *     We join them as-is — no re-sort, no re-derivation from raw
 *     `translation.phrases` (that would break clip mode).
 *
 * Per block:
 *   - `originalIndices` = the group's element `initial_index`es (negative
 *     placeholders dropped, de-duped, ascending). Using the GROUP's element
 *     indices — not the phrases' — means any active phrase inside the paragraph
 *     lights the row, because `AlignedBlocksView`'s active test is
 *     `originalIndices.includes(currentLead)`.
 *   - `translatedValue` = the group's phrases' `translatedValue` joined in
 *     `phrasesByGroup[i]`'s existing order.
 *   - `lead` = smallest source index; `id` = `para:<lead>`.
 *
 * Groups with no source indices (`lead === Infinity`) are dropped — no row to
 * align to. Output is sorted ascending by `lead`.
 *
 * PLAT-10945: a group ABSORBED by a correction spanning a paragraph boundary
 * emits no row of its own; its source positions are folded onto the row carrying
 * that correction, so one row shows both paragraphs' original-language words
 * beside the single correction instead of a row with an empty translated side.
 * See {@link resolveAbsorbedParagraphs} for the rule and its three refusals.
 */
export function buildParagraphBlocks(
  groups: readonly GroupWithSpeakerInfo[],
  phrasesByGroup: readonly TranslatedSortedPhrase[][],
  // PLAT-10209 H-A: clip-scoped mode feeds groups derived from a clip-LOCAL,
  // MONTAGE-ordered element array; preserve that order instead of re-sorting by
  // lead (which would re-impose story order and break the montage). The default
  // (Content Studio) path leaves this false → ascending-by-lead, unchanged
  // (its groups are already lead-ordered, so the sort is a no-op there anyway).
  preserveOrder = false,
  // PLAT-11717 — the array the original column RENDERS, needed to emit
  // `arraySpan` in that array's coordinates (see {@link sourcePositionsOf}).
  // Omit it and blocks carry NO `arraySpan`: a span in the groups' own
  // `filteredElements` coordinates is not a span the consumer can read, so the
  // honest value is none. Callers that render (`AlignedBlocksView`) pass it.
  sourceElements?: readonly TranscriptElement[],
): AlignedBlock[] {
  const sourcePositions = sourcePositionsOf(sourceElements);
  const { absorbed, extraIndicesByTarget, membersByTarget } =
    resolveAbsorbedParagraphs(groups, phrasesByGroup);
  const blocks: AlignedBlock[] = [];
  groups.forEach((group, i) => {
    // Absorbed: no row of its own. Its words are on the target's row below, so
    // nothing leaves the screen.
    if (absorbed.has(i)) return;
    const originalIndices = [
      ...new Set([
        ...groupSourceIndices(group),
        ...(extraIndicesByTarget.get(i) ?? []),
      ]),
    ].sort((a, b) => a - b);
    // PLAT-10266 (2026-07-28) — the row's OWN array-position span, INCLUDING the
    // `initial_index < 0` tokens `groupSourceIndices` drops. `renderOriginalWords`
    // uses it to reach a replaced word spliced in at this row's leading/trailing
    // edge (which anchors no identity and so fell outside the window), while
    // stopping at the row's bounds so a neighbour's inserts are never absorbed.
    // Spans the ABSORBED groups too (PLAT-10945): their words render on this row,
    // and a replacement among them has no source index to be folded in above.
    //
    // PLAT-11717: resolve each element's position in the RENDERED array rather
    // than reading its `index`, which is a position into the hook's
    // `filteredElements` — a different coordinate system (see `arraySpan`).
    let from = Infinity;
    let to = -Infinity;
    for (const memberIdx of [i, ...(membersByTarget.get(i) ?? [])]) {
      for (const { element } of groups[memberIdx].elements) {
        const pos = sourcePositions?.get(element);
        if (pos === undefined) continue;
        if (pos < from) from = pos;
        if (pos > to) to = pos;
      }
    }
    const arraySpan = from === Infinity ? undefined : { from, to };
    // Keep EVERY phrase, including empty/whitespace ones, with its anchor +
    // carried `phrase` — parity with the inline prose view (a `.filter(value
    // .length > 0)` here, ported from an old cut, dropped them and broke the
    // shared `data-initial-index` anchor set). `joinPhraseValues` already no-ops
    // empties for the display string, so `translatedValue` is unaffected.
    const translatedPhrases = (phrasesByGroup[i] ?? []).map(p => ({
      lead: p.lead,
      value: p.phrase.translatedValue ?? '',
      phrase: p.phrase,
    }));
    const translatedValue = joinPhraseValues(
      translatedPhrases.map(p => p.value),
    );
    const lead = minIndex(originalIndices);
    blocks.push({
      id: `para:${lead}`,
      originalIndices,
      arraySpan,
      translatedValue,
      translatedPhrases,
      lead,
    });
  });

  const kept = blocks.filter(b => Number.isFinite(b.lead));
  return preserveOrder ? kept : kept.sort((a, b) => a.lead - b.lead);
}

/**
 * PLAT-10689 — run-grouped Segment/Fragment layout for montage-reordered clips.
 *
 * Builds blocks by grouping the clip's on-screen content into MAXIMAL CONTIGUOUS
 * PLAYBACK RUNS that share an owner — the translation SEGMENT in Segment mode,
 * the PHRASE in Fragment mode — instead of one block per whole segment
 * ({@link buildAlignedBlocks}).
 *
 * Why: `sourceElements` is in PLAYBACK order. A montage-reordered clip scatters a
 * segment's fragments across playback (other segments play between them), so the
 * one-block-per-segment layout placed the whole segment at its earliest word,
 * dragging its later fragments up and pushing the interposing segments down —
 * "learned five years…" and "moment of inflection…" showed far too early.
 * Grouping by contiguous playback run lets a montage-split segment occupy
 * MULTIPLE rows at their true playback positions, exactly like Paragraph view
 * does at paragraph granularity, so the original column reads in playback order.
 * An unsplit segment is one contiguous run → one row (unchanged).
 *
 * Straddling fragments: a single fragment can itself span a gap (a moved word, or
 * a phrase whose middle was reordered out). Its ONE `translatedValue` can only
 * live in one row, so it is assigned to the run holding its EARLIEST-PLAYBACK
 * element (its lead run, matching Paragraph view). The fragment's later, detached
 * words still render in whatever run they physically fall in — the original stays
 * in playback order. A run left with original words but NO fragment anchored to
 * it (a lone moved word, e.g. a sentence-initial "And" relocated to the end) is
 * NOT orphaned into an empty-translation row: its words are folded back into the
 * run that holds their fragment's translation, so they render as trailing text on
 * that sentence's row.
 *
 * Clip-path only: `sourceElements` here is already the clip-LOCAL, montage-ordered
 * subset (`deriveClipScopedElements`), so walking it is inherently clip-scoped —
 * no `AlignedBlocksScope` argument is needed (only in-clip owners get runs).
 * `segments`/`activePhrases` describe the whole story; the walk keeps only what
 * is on screen. Content Studio / non-reordered clips keep the plain
 * {@link buildAlignedBlocks} path (array order == story order there, so every
 * segment is already one contiguous run — this would produce the same rows).
 *
 * Owner + fragment resolution is UUID-first (`source_uuid ?? id` vs each
 * fragment's `originalElementIds`), integer `initial_index` as the per-element
 * fallback — the same drift-correct precedence the rest of PLAT-10611 uses.
 *
 * Pure (no React/MobX/store); exported for unit testing.
 */
export function buildRunGroupedBlocks(
  viewMode: 'segment' | 'fragment',
  segments: readonly TranslationSegment[],
  activePhrases: readonly TranslationPhrase[],
  sourceElements: readonly TranscriptElement[],
): AlignedBlock[] {
  // Owners: one per segment (Segment mode) or per active phrase (Fragment mode).
  // Fragment ownerIds key off the array index so two phrases sharing a source
  // lead (a malformed-data edge) stay distinct owners / rows.
  const owners: { ownerId: string; fragments: readonly TranslationPhrase[] }[] =
    viewMode === 'segment'
      ? segments.map(s => ({ ownerId: s.id, fragments: s.fragments }))
      : activePhrases.map((p, i) => ({ ownerId: `f${i}`, fragments: [p] }));

  // element key -> owner id / owning fragment (UUID first, integer fallback).
  const ownerByUuid = new Map<string, string>();
  const ownerByIndex = new Map<number, string>();
  const fragmentByUuid = new Map<string, TranslationPhrase>();
  const fragmentByIndex = new Map<number, TranslationPhrase>();
  for (const { ownerId, fragments } of owners) {
    for (const f of fragments) {
      if (f.originalElementIds) {
        for (const id of f.originalElementIds) {
          ownerByUuid.set(id, ownerId);
          fragmentByUuid.set(id, f);
        }
      }
      for (const idx of f.originalIndices) {
        ownerByIndex.set(idx, ownerId);
        fragmentByIndex.set(idx, f);
      }
    }
  }

  // element key -> array position (= playback position). First write wins so a
  // reused UUID / index resolves to its earliest occurrence.
  const posByUuid = new Map<string, number>();
  const posByIndex = new Map<number, number>();
  sourceElements.forEach((el, pos) => {
    const uuid = el.source_uuid ?? el.id;
    if (uuid !== undefined && !posByUuid.has(uuid)) posByUuid.set(uuid, pos);
    if (el.initial_index >= 0 && !posByIndex.has(el.initial_index)) {
      posByIndex.set(el.initial_index, pos);
    }
  });

  const ownerOf = (el: TranscriptElement): string | undefined => {
    const uuid = el.source_uuid ?? el.id;
    const byId = uuid !== undefined ? ownerByUuid.get(uuid) : undefined;
    if (byId !== undefined) return byId;
    return el.initial_index >= 0
      ? ownerByIndex.get(el.initial_index)
      : undefined;
  };
  const fragmentOf = (el: TranscriptElement): TranslationPhrase | undefined => {
    const uuid = el.source_uuid ?? el.id;
    const byId = uuid !== undefined ? fragmentByUuid.get(uuid) : undefined;
    if (byId !== undefined) return byId;
    return el.initial_index >= 0
      ? fragmentByIndex.get(el.initial_index)
      : undefined;
  };
  // A fragment's anchor = the earliest PLAYBACK position among its elements.
  const anchorPosOf = (f: TranslationPhrase): number => {
    let min = Infinity;
    if (f.originalElementIds) {
      for (const id of f.originalElementIds) {
        const p = posByUuid.get(id);
        if (p !== undefined && p < min) min = p;
      }
    }
    for (const idx of f.originalIndices) {
      const p = posByIndex.get(idx);
      if (p !== undefined && p < min) min = p;
    }
    return min;
  };

  // 1. Element-runs: maximal consecutive content elements sharing an owner.
  //    Whitespace / synthetic spacers / unowned content carry no owner and are
  //    transparent — they neither start nor break a run (the spacing is rebuilt
  //    from the array slice in `renderOriginalWords`).
  interface Run {
    ownerId: string;
    uuids: string[];
    indices: number[];
    positions: number[];
    firstPos: number;
  }
  const runs: Run[] = [];
  const runIndexByPos = new Map<number, number>();
  let curOwner: string | undefined;
  let curRun: Run | undefined;
  sourceElements.forEach((el, pos) => {
    const owner = ownerOf(el);
    if (owner === undefined) return;
    if (owner !== curOwner || curRun === undefined) {
      curRun = {
        ownerId: owner,
        uuids: [],
        indices: [],
        positions: [],
        firstPos: pos,
      };
      runs.push(curRun);
      curOwner = owner;
    }
    const uuid = el.source_uuid ?? el.id;
    if (uuid !== undefined) curRun.uuids.push(uuid);
    if (el.initial_index >= 0) curRun.indices.push(el.initial_index);
    curRun.positions.push(pos);
    runIndexByPos.set(pos, runs.length - 1);
  });

  // 2. Assign each fragment to the run holding its earliest-playback element.
  const fragsByRun = new Map<number, TranslationPhrase[]>();
  for (const { fragments } of owners) {
    for (const f of fragments) {
      const ap = anchorPosOf(f);
      if (!Number.isFinite(ap)) continue;
      const ri = runIndexByPos.get(ap);
      if (ri === undefined) continue;
      const list = fragsByRun.get(ri);
      if (list) list.push(f);
      else fragsByRun.set(ri, [f]);
    }
  }

  // 3. Orphan runs (no fragment anchored here — e.g. a lone moved word): fold
  //    their elements into the run that holds each element's fragment (its
  //    anchor run, which always has that fragment, so it is a real row). This
  //    keeps the moved word as trailing text on its sentence rather than an
  //    empty-translation row.
  const extraByRun = new Map<number, { uuids: string[]; indices: number[] }>();
  runs.forEach((run, ri) => {
    if (fragsByRun.has(ri)) return;
    for (const pos of run.positions) {
      const el = sourceElements[pos];
      const f = fragmentOf(el);
      if (!f) continue;
      const target = runIndexByPos.get(anchorPosOf(f));
      if (target === undefined) continue;
      const extra = extraByRun.get(target) ?? { uuids: [], indices: [] };
      const uuid = el.source_uuid ?? el.id;
      if (uuid !== undefined) extra.uuids.push(uuid);
      if (el.initial_index >= 0) extra.indices.push(el.initial_index);
      extraByRun.set(target, extra);
    }
  });

  // 4. Emit one block per REAL run (orphan runs dropped — their elements were
  //    folded above), in playback order (= run creation order).
  const blocks: AlignedBlock[] = [];
  runs.forEach((run, ri) => {
    const frags = fragsByRun.get(ri);
    if (!frags) return;
    const extra = extraByRun.get(ri);
    const uuids = [
      ...new Set(extra ? [...run.uuids, ...extra.uuids] : run.uuids),
    ];
    const indices = [
      ...new Set(extra ? [...run.indices, ...extra.indices] : run.indices),
    ].sort((a, b) => a - b);
    const lead = minIndex(indices);
    // Translated units in STORY-lead order — parity with the inline view and the
    // active-phrase range read in `AlignedBlocksView`, which walks
    // `translatedPhrases` by ascending lead.
    const translatedPhrases = frags
      .map(f => ({
        lead: minIndex(fragmentIndices(f)),
        value: f.translatedValue ?? '',
        phrase: f,
      }))
      .sort((a, b) => a.lead - b.lead);
    blocks.push({
      id: `${run.ownerId}#${lead}`,
      originalIndices: indices,
      originalElementIds: uuids.length > 0 ? uuids : undefined,
      translatedValue: joinPhraseValues(
        translatedPhrases.map(p => p.value).filter(v => v.length > 0),
      ),
      translatedPhrases,
      lead,
    });
  });
  return blocks.filter(b => Number.isFinite(b.lead));
}

/**
 * Project one language's phrases into another language's blocks — the proxy
 * REFERENCE column (PLAT-10655): the rows stay tiled by the REVIEWED
 * language's blocks, and each proxy phrase lands in the block whose
 * `[min..max]` source range contains the phrase's lead (blocks tile the source
 * contiguously; a boundary-straddling phrase goes by its lead). Returns
 * blockId → lead-sorted `{lead, value, phrase}` units, the same shape as
 * `AlignedBlock.translatedPhrases`. Pure; exported for unit tests.
 */
export function projectPhrasesIntoBlocks(
  blocks: readonly Pick<AlignedBlock, 'id' | 'originalIndices'>[],
  phrases: readonly TranslationPhrase[],
): Map<string, { lead: number; value: string; phrase: TranslationPhrase }[]> {
  const byId = new Map<
    string,
    { lead: number; value: string; phrase: TranslationPhrase }[]
  >();
  // Ranges ascending by min — clip-mode blocks arrive in PLAY order, so sort a
  // copy for the two-pointer walk and address results by id.
  const ranges = blocks
    .filter(b => b.originalIndices.length > 0)
    .map(b => ({
      id: b.id,
      min: minIndex(b.originalIndices),
      max: b.originalIndices.reduce((m, i) => (i > m ? i : m), -Infinity),
    }))
    .sort((a, b) => a.min - b.min);
  const units = fragmentsToTranslatedPhrases(phrases);
  let r = 0;
  for (const unit of units) {
    while (r < ranges.length && ranges[r].max < unit.lead) r++;
    if (r >= ranges.length) break;
    const range = ranges[r];
    if (unit.lead < range.min) continue; // falls in a gap between blocks
    const list = byId.get(range.id);
    if (list) list.push(unit);
    else byId.set(range.id, [unit]);
  }
  return byId;
}
